// TestResultPill
// ---------------
// Single source for the visual pill that surfaces a test's result:
// "Completed" / "Test Failed" / "Stopped Early" / "Live" / "Unknown".
// Two size variants share one definition so the result chip on a
// Dashboard card / Applications row, the larger one in the
// WebApplication page's Security Status header, and any future
// surface all stay in lockstep with each other and with
// testStatusLabel.ts (which owns the label + tone).
//
// Variants:
//   sm — used inside AppStatusPill's result branch (cards, rows).
//        Compact 10px text + dot, no ring; sits next to URLs and
//        meta within tight horizontal space.
//   md — used in TestDetails' Security Status header. Slightly
//        larger (12px text + ring), no dot, intended to read as a
//        page-level badge rather than an inline indicator.

import React from "react";
import {
  TestResultLabel,
  STATUS_TONE_STYLES,
} from "@/utils/testStatusLabel";

interface TestResultPillProps {
  resultLabel: TestResultLabel;
  // Picks the chrome variant — see the file-level header.
  // Defaults to "sm" which matches the inline / row context that
  // appears most often in the app.
  size?: "sm" | "md";
  // Extra utility classes — e.g. `shrink-0` on the Applications row
  // where the pill sits beside a truncating app name. Optional.
  className?: string;
}

export default function TestResultPill({
  resultLabel,
  size = "sm",
  className = "",
}: TestResultPillProps) {
  const tone = STATUS_TONE_STYLES[resultLabel.tone];
  const trailing = className ? ` ${className}` : "";

  if (size === "md") {
    // Header-level chip. No dot — the badge sits in a header bar
    // alongside other status indicators (Testing Live etc.) that
    // each have their own visual; the ring + text-xs serves the
    // same role as the dot does in the sm variant.
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tone.bgColor} ${tone.textColor} ${tone.ringColor}${trailing}`}
      >
        {resultLabel.label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${tone.bgColor} ${tone.textColor}${trailing}`}
    >
      <span className={`inline-flex h-1.5 w-1.5 rounded-full ${tone.dotColor}`} />
      {resultLabel.label}
    </span>
  );
}
